import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatFullDateTimeIST } from '../../utils/dateUtils';
import { formatMilli } from '../../utils/formatUtils';
import { 
  Search, User, Phone, Wallet, Calendar, ChevronLeft, ChevronRight,
  Mail, MapPin, CreditCard, Shield, Edit, Save, X, Loader2, Users, Gift, RefreshCw, Trash2, AlertTriangle, Edit2, Undo2
} from 'lucide-react';
import { BackButton, PageSkeleton } from '../../components/common';

export const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [processingReferral, setProcessingReferral] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [settingBalance, setSettingBalance] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Balance Decimal Rounding State
  const [showDecimalFixModal, setShowDecimalFixModal] = useState(false);
  const [decimalPreviewData, setDecimalPreviewData] = useState(null);
  const [decimalPreviewLoading, setDecimalPreviewLoading] = useState(false);
  const [decimalFixLoading, setDecimalFixLoading] = useState(false);
  
  const limit = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getUsers(page * limit, limit, search);
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  // Balance Decimal Rounding Functions
  const handlePreviewDecimalFix = async () => {
    setDecimalPreviewLoading(true);
    setShowDecimalFixModal(true);
    try {
      const response = await adminAPI.previewBalanceDecimalIssues();
      setDecimalPreviewData(response.data);
    } catch (error) {
      toast.error('Failed to preview decimal issues');
      setShowDecimalFixModal(false);
    } finally {
      setDecimalPreviewLoading(false);
    }
  };

  const handleApplyDecimalFix = async () => {
    setDecimalFixLoading(true);
    try {
      const response = await adminAPI.applyBalanceDecimalFix();
      toast.success(`Successfully truncated ${response.data.users_fixed} user balances`);
      setShowDecimalFixModal(false);
      setDecimalPreviewData(null);
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error('Failed to apply decimal fix');
    } finally {
      setDecimalFixLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const openUserDetails = async (user) => {
    try {
      // Fetch full user details
      const response = await adminAPI.getUserDetails(user.user_id);
      // Response contains { user, transactions, vouchers, stats }
      const userData = response.data.user || response.data;
      setSelectedUser({
        ...userData,
        transactions: response.data.transactions || [],
        vouchers: response.data.vouchers || [],
        stats: response.data.stats || {}
      });
      setEditData({
        name: userData.name || '',
        email: userData.email || '',
        address: userData.address || '',
        milli_balance: userData.milli_balance || 0,
      });
      setEditMode(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to load user details';
      toast.error(errorMsg);
      // Refresh the user list in case of stale data
      if (error.response?.status === 404) {
        fetchUsers();
      }
    }
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      await adminAPI.updateUser(selectedUser.user_id, editData);
      toast.success('User details updated successfully');
      setEditMode(false);
      // Refresh user details
      const response = await adminAPI.getUserDetails(selectedUser.user_id);
      const userData = response.data.user || response.data;
      setSelectedUser({
        ...userData,
        transactions: response.data.transactions || [],
        vouchers: response.data.vouchers || [],
        stats: response.data.stats || {}
      });
      // Refresh list
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleProcessReferralReward = async () => {
    if (!selectedUser?.referred_by) {
      toast.error('This user was not referred by anyone');
      return;
    }
    
    if (!window.confirm(`Process referral reward for ${selectedUser.name}? This will credit rewards to both the referrer and this user.`)) {
      return;
    }
    
    setProcessingReferral(true);
    try {
      const response = await adminAPI.processReferralReward(selectedUser.user_id);
      const { referee, referrer } = response.data;
      toast.success(`Referral rewards credited! ${referee.name}: +${referee.reward_credited} Milli, ${referrer.name}: +${referrer.reward_credited} Milli`);
      // Refresh user details
      const userResponse = await adminAPI.getUserDetails(selectedUser.user_id);
      const userData = userResponse.data.user || userResponse.data;
      setSelectedUser({
        ...userData,
        transactions: userResponse.data.transactions || [],
        vouchers: userResponse.data.vouchers || [],
        stats: userResponse.data.stats || {}
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process referral reward');
    } finally {
      setProcessingReferral(false);
    }
  };

  const handleRecalculateBalance = async () => {
    if (!window.confirm(`Recalculate balance for ${selectedUser.name}?\n\nThis will recompute the milli balance based on all COMPLETED transactions only. Failed/pending transactions will be excluded.`)) {
      return;
    }
    
    setRecalculating(true);
    try {
      const response = await adminAPI.recalculateUserBalance(selectedUser.user_id);
      const { old_balance, new_balance, difference, breakdown } = response.data;
      
      let message = `Balance recalculated!\n\nOld: ${old_balance} Milli's\nNew: ${new_balance} Milli's\nDifference: ${difference >= 0 ? '+' : ''}${difference}`;
      
      if (breakdown.failed_transactions.length > 0) {
        message += `\n\n${breakdown.failed_transactions.length} failed transaction(s) were excluded.`;
      }
      
      toast.success(message);
      
      // Refresh user details
      const userResponse = await adminAPI.getUserDetails(selectedUser.user_id);
      const userData = userResponse.data.user || userResponse.data;
      setSelectedUser({
        ...userData,
        transactions: userResponse.data.transactions || [],
        vouchers: userResponse.data.vouchers || [],
        stats: userResponse.data.stats || {}
      });
      setEditData(prev => ({ ...prev, milli_balance: userData.milli_balance }));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate balance');
    } finally {
      setRecalculating(false);
    }
  };

  const handleSetBalance = async () => {
    const newBalanceStr = prompt(
      `Set new balance for ${selectedUser.name}\n\n` +
      `Current balance: ${selectedUser.milli_balance || 0} Milli's\n\n` +
      `Enter the correct balance (e.g., 150.50):`
    );
    
    if (newBalanceStr === null) return; // User cancelled
    
    const newBalance = parseFloat(newBalanceStr);
    if (isNaN(newBalance)) {
      toast.error('Please enter a valid number');
      return;
    }
    
    if (newBalance < 0) {
      if (!window.confirm(`Are you sure you want to set a NEGATIVE balance of ${newBalance}?`)) {
        return;
      }
    }
    
    setSettingBalance(true);
    try {
      const response = await adminAPI.setUserBalance(selectedUser.user_id, newBalance);
      const { old_balance, new_balance } = response.data;
      
      toast.success(`Balance updated!\n\nOld: ${old_balance} Milli's\nNew: ${new_balance} Milli's`);
      
      // Refresh user details
      const userResponse = await adminAPI.getUserDetails(selectedUser.user_id);
      const userData = userResponse.data.user || userResponse.data;
      setSelectedUser({
        ...userData,
        transactions: userResponse.data.transactions || [],
        vouchers: userResponse.data.vouchers || [],
        stats: userResponse.data.stats || {}
      });
      setEditData(prev => ({ ...prev, milli_balance: userData.milli_balance }));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set balance');
    } finally {
      setSettingBalance(false);
    }
  };

  const handleUndoRecalculate = async () => {
    if (!selectedUser.balance_before_recalculate && selectedUser.balance_before_recalculate !== 0) {
      toast.error('No previous balance to restore. Undo is only available after a recalculate operation.');
      return;
    }
    
    if (!window.confirm(
      `Undo recalculation for ${selectedUser.name}?\n\n` +
      `This will restore the balance from ${selectedUser.milli_balance} to ${selectedUser.balance_before_recalculate} Milli's.`
    )) {
      return;
    }
    
    setUndoing(true);
    try {
      const response = await adminAPI.undoRecalculateBalance(selectedUser.user_id);
      const { restored_balance, reverted_from } = response.data;
      
      toast.success(`Balance restored!\n\nFrom: ${reverted_from} Milli's\nTo: ${restored_balance} Milli's`);
      
      // Refresh user details
      const userResponse = await adminAPI.getUserDetails(selectedUser.user_id);
      const userData = userResponse.data.user || userResponse.data;
      setSelectedUser({
        ...userData,
        transactions: userResponse.data.transactions || [],
        vouchers: userResponse.data.vouchers || [],
        stats: userResponse.data.stats || {}
      });
      setEditData(prev => ({ ...prev, milli_balance: userData.milli_balance }));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo recalculation');
    } finally {
      setUndoing(false);
    }
  };

  const handleDeleteUser = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      const response = await adminAPI.forceDeleteUser(selectedUser.user_id);
      const { deleted_counts } = response.data;
      
      toast.success(
        `User deleted successfully!\n` +
        `Removed: ${deleted_counts.transactions} transactions, ${deleted_counts.vouchers} vouchers`
      );
      
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-users-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <BackButton to="/admin" />
          <h1 className="font-serif text-xl text-navy font-semibold">User Management</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search by name, phone, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 h-12"
            />
          </div>
        </form>

        {/* Stats */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            Showing {users.length} of {total} users
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviewDecimalFix}
              className="text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Fix Decimal Balances
            </Button>
            <p className="text-xs text-muted-foreground">Click on a user to view/edit details</p>
          </div>
        </div>

        {/* User List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : users.length === 0 ? (
          <Card className="border-gold/20">
            <CardContent className="py-12 text-center">
              <User className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No users found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {users.map((u) => (
              <Card 
                key={u.user_id} 
                className="border-gold/20 cursor-pointer hover:shadow-md hover:border-primary/30 transition-all"
                onClick={() => openUserDetails(u)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-navy">{u.name}</p>
                          {u.kyc_verified && (
                            <Badge className="bg-green-100 text-green-700 text-xs">KYC</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          <span>{u.phone || 'No phone'}</span>
                        </div>
                        {u.email && (
                          <p className="text-xs text-muted-foreground mt-1">{u.email}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>Joined {formatFullDateTimeIST(u.created_at).split(',')[0]}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Wallet className="w-4 h-4 text-primary" />
                        <span className="font-semibold text-navy">
                          {formatCurrency(u.milli_balance || 0)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">Millis</p>
                      {u.bank_account_number && (
                        <Badge variant="outline" className="mt-2 text-xs">
                          Bank Added
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      <Dialog open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-navy flex items-center gap-2">
              <User className="w-5 h-5" />
              User Details
            </DialogTitle>
            <DialogDescription>
              View and modify user information
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-6 pt-4">
              {/* User Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg text-navy">{selectedUser.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedUser.phone}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {selectedUser.kyc_verified && (
                    <Badge className="bg-green-100 text-green-700">KYC Verified</Badge>
                  )}
                </div>
              </div>

              {/* Balance Card */}
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Current Balance</p>
                      <p className="text-2xl font-bold text-navy">
                        {formatCurrency(selectedUser.milli_balance || 0)} Millis
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Wallet className="w-10 h-10 text-primary/50" />
                      <div className="flex gap-1 flex-wrap justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRecalculateBalance}
                          disabled={recalculating}
                          className="text-xs gap-1"
                        >
                          {recalculating ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <RefreshCw className="w-3 h-3" />
                          )}
                          Recalculate
                        </Button>
                        {(selectedUser.balance_before_recalculate !== undefined && selectedUser.balance_before_recalculate !== null) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleUndoRecalculate}
                            disabled={undoing}
                            className="text-xs gap-1 border-red-500 text-red-600 hover:bg-red-50"
                          >
                            {undoing ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Undo2 className="w-3 h-3" />
                            )}
                            Undo
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSetBalance}
                          disabled={settingBalance}
                          className="text-xs gap-1 border-amber-500 text-amber-600 hover:bg-amber-50"
                        >
                          {settingBalance ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Edit2 className="w-3 h-3" />
                          )}
                          Set
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Editable Fields */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-navy">Personal Information</h3>
                  {!editMode ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditMode(true)}
                      className="gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditMode(false);
                          setEditData({
                            name: selectedUser.name || '',
                            email: selectedUser.email || '',
                            address: selectedUser.address || '',
                            milli_balance: selectedUser.milli_balance || 0,
                          });
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveUser}
                        disabled={saving}
                        className="gap-2 gold-gradient text-navy"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground flex items-center gap-2">
                      <User className="w-3 h-3" /> Full Name
                    </Label>
                    {editMode ? (
                      <Input
                        value={editData.name}
                        onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter full name"
                      />
                    ) : (
                      <p className="font-medium">{selectedUser.name || '-'}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground flex items-center gap-2">
                      <Mail className="w-3 h-3" /> Email
                    </Label>
                    {editMode ? (
                      <Input
                        type="email"
                        value={editData.email}
                        onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="Enter email"
                      />
                    ) : (
                      <p className="font-medium">{selectedUser.email || '-'}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground flex items-center gap-2">
                      <MapPin className="w-3 h-3" /> Address
                    </Label>
                    {editMode ? (
                      <Textarea
                        value={editData.address}
                        onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="Enter address"
                        rows={3}
                      />
                    ) : (
                      <p className="font-medium">{selectedUser.address || '-'}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground flex items-center gap-2">
                      <Wallet className="w-3 h-3" /> Milli Balance (Admin Override)
                    </Label>
                    {editMode ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={editData.milli_balance}
                        onChange={(e) => setEditData(prev => ({ ...prev, milli_balance: parseFloat(e.target.value) || 0 }))}
                        placeholder="Enter balance"
                      />
                    ) : (
                      <p className="font-medium">{formatCurrency(selectedUser.milli_balance || 0)}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Read-only Information */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-navy">Additional Information</h3>
                
                <div>
                  <p className="text-xs text-muted-foreground">Phone Number</p>
                  <p className="font-medium">{selectedUser.phone}</p>
                </div>
                
                <div>
                  <p className="text-xs text-muted-foreground">User ID</p>
                  <p className="font-mono text-xs break-all">{selectedUser.user_id}</p>
                </div>

                {selectedUser.aadhaar_number && (
                  <div>
                    <p className="text-xs text-muted-foreground">Aadhaar Number</p>
                    <p className="font-mono">XXXX XXXX {selectedUser.aadhaar_number.slice(-4)}</p>
                  </div>
                )}

                {selectedUser.bank_account_number && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Bank Name</p>
                      <p className="font-medium">{selectedUser.bank_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Account Number</p>
                      <p className="font-mono">****{selectedUser.bank_account_number.slice(-4)}</p>
                    </div>
                  </div>
                )}

                {selectedUser.has_nominee && (
                  <div className="pt-4 border-t">
                    <p className="text-xs text-muted-foreground flex items-center gap-2 mb-2">
                      <Users className="w-3 h-3" /> Nominee Details
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="font-medium">{selectedUser.nominee_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Relationship</p>
                        <p className="font-medium">{selectedUser.nominee_relationship}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs text-muted-foreground">Joined</p>
                  <p className="font-medium">
                    {formatFullDateTimeIST(selectedUser.created_at)}
                  </p>
                </div>

                {/* Referral Section */}
                {selectedUser.referred_by && (
                  <div className="pt-4 border-t">
                    <p className="text-xs text-muted-foreground flex items-center gap-2 mb-2">
                      <Gift className="w-3 h-3" /> Referral Information
                    </p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Referred By</p>
                        <p className="font-mono text-xs">{selectedUser.referred_by}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleProcessReferralReward}
                        disabled={processingReferral}
                        className="w-full border-green-300 text-green-700 hover:bg-green-50"
                      >
                        {processingReferral ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Gift className="w-4 h-4 mr-2" />
                        )}
                        Process Referral Reward
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        Click if referral reward was not automatically credited
                      </p>
                    </div>
                  </div>
                )}

                {/* Danger Zone - Delete User */}
                <div className="pt-4 border-t border-red-200">
                  <p className="text-xs text-red-600 font-medium flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-3 h-3" /> Danger Zone
                  </p>
                  
                  {!showDeleteConfirm ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="delete-user-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete User & All Data
                    </Button>
                  ) : (
                    <div className="space-y-3 p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-red-700">This action cannot be undone!</p>
                          <p className="text-red-600 text-xs mt-1">
                            This will permanently delete the user and all their:
                          </p>
                          <ul className="text-red-600 text-xs mt-1 list-disc list-inside">
                            <li>Transactions (buy, sell, redeem)</li>
                            <li>Gifts (sent & received)</li>
                            <li>Vouchers</li>
                            <li>Referral data</li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-xs text-red-700">Type DELETE to confirm:</Label>
                        <Input
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="Type DELETE"
                          className="border-red-300 focus:border-red-500"
                          data-testid="delete-confirm-input"
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmText('');
                          }}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleDeleteUser}
                          disabled={deleting || deleteConfirmText !== 'DELETE'}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                          data-testid="confirm-delete-btn"
                        >
                          {deleting ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-2" />
                          )}
                          Delete Permanently
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Use this to remove test accounts or duplicate users
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Balance Decimal Fix Modal */}
      <Dialog open={showDecimalFixModal} onOpenChange={setShowDecimalFixModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Fix Balance Decimal Places
            </DialogTitle>
            <DialogDescription>
              Truncate all milli_balance values to 2 decimal places (cuts off extra digits without rounding up).
            </DialogDescription>
          </DialogHeader>

          {decimalPreviewLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gold" />
              <span className="ml-2">Scanning users...</span>
            </div>
          ) : decimalPreviewData ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Users Scanned:</span>
                    <span className="ml-2 font-medium">{decimalPreviewData.total_users_scanned}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Users Affected:</span>
                    <span className="ml-2 font-medium text-amber-600">{decimalPreviewData.affected_users_count}</span>
                  </div>
                </div>
              </div>

              {/* Affected Users Table */}
              {decimalPreviewData.affected_users_count > 0 ? (
                <>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-muted/30 px-4 py-2 text-xs font-medium grid grid-cols-5 gap-2">
                      <span>Phone</span>
                      <span>Name</span>
                      <span className="text-right">Current</span>
                      <span className="text-right">Truncated</span>
                      <span className="text-right">Diff</span>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {decimalPreviewData.affected_users.map((user, idx) => (
                        <div 
                          key={user.user_id} 
                          className={`px-4 py-2 text-xs grid grid-cols-5 gap-2 ${idx % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
                        >
                          <span className="font-mono">{user.phone}</span>
                          <span className="truncate">{user.name}</span>
                          <span className="text-right font-mono text-amber-600" title={user.current_balance}>
                            {user.current_balance_str || user.current_balance.toString()}
                          </span>
                          <span className="text-right font-mono text-green-600">{user.truncated_balance?.toFixed(2) || user.rounded_balance?.toFixed(2)}</span>
                          <span className={`text-right font-mono ${user.difference > 0 ? 'text-red-500' : 'text-blue-500'}`}>
                            {user.difference > 0 ? '+' : ''}{user.difference.toExponential(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDecimalFixModal(false);
                        setDecimalPreviewData(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleApplyDecimalFix}
                      disabled={decimalFixLoading}
                      className="bg-gold hover:bg-gold/90 text-white"
                    >
                      {decimalFixLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Applying...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Apply Fix to {decimalPreviewData.affected_users_count} Users
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="text-green-600 text-lg mb-2">✓ All balances are clean!</div>
                  <p className="text-sm text-muted-foreground">
                    No users have balance values with more than 2 decimal places.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => {
                      setShowDecimalFixModal(false);
                      setDecimalPreviewData(null);
                    }}
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
